import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Alert,
    Chip,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    LinearProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { staffService } from '../services/staffService';
import { warmupService, SystemHealth, ServiceHealth } from '../services/warmupService';
import { config } from '../config/environment';

const BackendTestPage: React.FC = () => {
    const { user, backendType } = useAuth();
    const [staffStats, setStaffStats] = useState<any>(null);
    const [staffList, setStaffList] = useState<any>(null);
    const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
    const [loading, setLoading] = useState(false);
    const [healthLoading, setHealthLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const checkSystemHealth = async () => {
        setHealthLoading(true);
        setError(null);
        try {
            const health = await warmupService.checkSystemHealth();
            setSystemHealth(health);
        } catch (err: any) {
            setError(err.message || 'Failed to check system health');
        } finally {
            setHealthLoading(false);
        }
    };

    const warmupAllServices = async () => {
        setHealthLoading(true);
        setError(null);
        try {
            const health = await warmupService.warmupServices();
            setSystemHealth(health);
        } catch (err: any) {
            setError(err.message || 'Failed to warmup services');
        } finally {
            setHealthLoading(false);
        }
    };

    const testStaffStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const stats = await staffService.getStaffStats();
            setStaffStats(stats);
        } catch (err: any) {
            setError(err.message || 'Failed to get staff stats');
        } finally {
            setLoading(false);
        }
    };

    const testStaffList = async () => {
        setLoading(true);
        setError(null);
        try {
            const staff = await staffService.getStaffProfiles({ page: 1, pageSize: 5 });
            setStaffList(staff);
        } catch (err: any) {
            setError(err.message || 'Failed to get staff list');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ready':
            case 'healthy':
                return 'success';
            case 'degraded':
                return 'warning';
            case 'error':
            case 'unavailable':
            case 'unhealthy':
                return 'error';
            default:
                return 'default';
        }
    };

    const getOverallStatusColor = (overall: string) => {
        switch (overall) {
            case 'healthy':
                return '#4caf50';
            case 'degraded':
                return '#ff9800';
            case 'unhealthy':
                return '#f44336';
            default:
                return '#757575';
        }
    };

    useEffect(() => {
        // Auto-check system health on component mount
        checkSystemHealth();
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Backend Services Test Page
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* System Health Overview */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            System Health Overview
                        </Typography>
                        <Box>
                            <Button 
                                variant="outlined" 
                                onClick={checkSystemHealth}
                                disabled={healthLoading}
                                sx={{ mr: 1 }}
                            >
                                {healthLoading ? <CircularProgress size={20} /> : 'Check Health'}
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={warmupAllServices}
                                disabled={healthLoading}
                            >
                                {healthLoading ? <CircularProgress size={20} /> : 'Warmup All'}
                            </Button>
                        </Box>
                    </Box>

                    {healthLoading && <LinearProgress sx={{ mb: 2 }} />}

                    {systemHealth && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                                    Overall Status:
                                </Typography>
                                <Chip 
                                    label={systemHealth.overall.toUpperCase()}
                                    color={getStatusColor(systemHealth.overall) as any}
                                    sx={{ 
                                        backgroundColor: getOverallStatusColor(systemHealth.overall),
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }}
                                />
                                <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                                    Last checked: {new Date(systemHealth.timestamp).toLocaleString()}
                                </Typography>
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Service</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Response Time</strong></TableCell>
                                            <TableCell><strong>Message</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {systemHealth.services.map((service: ServiceHealth) => (
                                            <TableRow key={service.service}>
                                                <TableCell>
                                                    <Typography variant="subtitle2">
                                                        {service.service}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={service.status}
                                                        color={getStatusColor(service.status) as any}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {service.responseTimeMs}ms
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                                        {service.message}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Staff Service Tests */}
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Staff Service Tests
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                                <Button 
                                    variant="outlined" 
                                    onClick={testStaffStats}
                                    disabled={loading}
                                    sx={{ mr: 1 }}
                                >
                                    {loading ? <CircularProgress size={20} /> : 'Test Staff Stats'}
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={testStaffList}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20} /> : 'Test Staff List'}
                                </Button>
                            </Box>

                            {staffStats && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Staff Statistics:
                                    </Typography>
                                    <Box sx={{ 
                                        backgroundColor: '#f5f5f5', 
                                        p: 2, 
                                        borderRadius: 1, 
                                        maxHeight: 300, 
                                        overflow: 'auto' 
                                    }}>
                                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                                            {JSON.stringify(staffStats, null, 2)}
                                        </pre>
                                    </Box>
                                </Box>
                            )}

                            {staffList && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Staff List (first 5):
                                    </Typography>
                                    <Box sx={{ 
                                        backgroundColor: '#f5f5f5', 
                                        p: 2, 
                                        borderRadius: 1, 
                                        maxHeight: 300, 
                                        overflow: 'auto' 
                                    }}>
                                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                                            {JSON.stringify(staffList, null, 2)}
                                        </pre>
                                    </Box>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                System Information
                            </Typography>
                            
                            <Typography variant="body2" gutterBottom>
                                <strong>Current User:</strong> {user?.email || 'Not logged in'}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                <strong>Backend Type:</strong> {backendType || 'Unknown'}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                <strong>Environment:</strong> {config.environment}
                            </Typography>
                            
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Service URLs:
                                </Typography>
                                {Object.entries(warmupService.getAllServiceUrls()).map(([name, url]) => (
                                    <Typography key={name} variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        <strong>{name}:</strong> {url}
                                    </Typography>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
};

export default BackendTestPage;
